// The DISCORD page of the hub CLAN panel (player-reach.md §3.9, R1311).
//
// The page title names it and its summary carries the state word, so the
// page draws its content alone, with no card round it (R1945). The words
// "the card" below mean this content.
// The owner or an officer of a clan sets one Discord webhook: the switch
// of the webhook at the top of the card (R2422), the URL, the language of
// the posts and one switch per clan post class in the order of
// `DISCORD_CLASSES`, each with an optional ROLE ID box: a post of the class
// pings that role (R1642). SAVE sends the five together
// (`PUT /api/clan/discord`); a change of a switch, of the language or of a
// role box waits in the card until SAVE. An empty URL box keeps the stored
// URL, which the server answers masked and never whole. A role box shows
// the stored id, and an empty role box clears the role. TEST posts one test message now and says what Discord
// answered. REMOVE deletes the webhook, and it takes the second press of the
// armed-control rule (R837).
//
// The state pill reads NOT SET, ON or OFF. OFF is the switch, or a webhook
// that Discord refused `failures` times in a row (a deleted webhook); the
// card then says so and asks for the URL of a new one.
//
// A member does not see the card: the CLAN panel mounts it for the owner
// and the officers alone, the same rank the server checks.
//
// The card owns no call state of its own. The CLAN panel runs every write
// through its one `call`, and `discordRefusalFor` gives the line of each
// refusal.

import { oneOf } from '../../../util/oneof';
import { button, valueChoice } from '../../choice/choice';
import {
  CODE_DISCORD_INVALID, CODE_DISCORD_NONE,
  deleteClanDiscord, getClanDiscord, putClanDiscord, testClanDiscord,
  type ClanDiscord, type DiscordTestResult,
} from '../../../net/api';
import {
  DISCORD_CLASSES, DISCORD_ROLE_ID_MAX, DISCORD_URL_MAX, LANGUAGES, type DiscordClass,
} from '../../../gen/balance';
import { hubDiscord as S } from '../../../gen/strings';
import type { ArmGate } from '../../arm';
import { TTL_ON_OPEN, type Cell } from '../hubstate';
import {
  armedButton, armedPress, ctlRow, errLine, esc, lede, pending, plain, srow, switchButton,
  type PanelContext,
} from '../panel';
import type { RunCall } from './pickup';

/** The cache key of the read. A write drops it. */
const DISCORD_CELL = 'clan-discord';

/** The `data-in` name of the URL box. */
export const DISCORD_URL_BOX = 'discord-url';

/** The `data-in` name of the ROLE ID box of one class (R1642). */
export function discordRoleBox(cls: DiscordClass): string {
  return `discord-role-${cls}`;
}

/** The name and the quiet line of one class switch. */
const CLASS_WORDS: Record<DiscordClass, [name: string, desc: string]> = {
  siege: [S.class_siege, S.class_siege_desc],
  members: [S.class_members, S.class_members_desc],
  clanwar: [S.class_clanwar, S.class_clanwar_desc],
  registration: [S.class_registration, S.class_registration_desc],
  milestones: [S.class_milestones, S.class_milestones_desc],
};

/** The line of each refusal of a webhook route, by the wire code. */
const REFUSALS: ReadonlyMap<string, string> = new Map([
  [CODE_DISCORD_INVALID, S.refusal_url],
  [CODE_DISCORD_NONE, S.refusal_none],
]);

/** What the panel says about one refusal of a webhook route, or '' for a
 *  code that is not one of them. A refused role id has its own line: the
 *  server names the field `roles` (R1642). */
export function discordRefusalFor(code: string, field = ''): string {
  if (code === CODE_DISCORD_INVALID && field === 'roles') return S.refusal_role;
  return REFUSALS.get(code) ?? '';
}

/** The language tag a press names, or undefined for a tag no language of
 *  the game carries. */
export function postLangOf(id: string | undefined): string | undefined {
  return LANGUAGES.find((l) => l.tag === id)?.tag;
}

/** The language of the posts: one saved value, an option list while the
 *  game speaks six languages or fewer (R2011). The DISCORD and the CHAT
 *  RELAY page each draw one, under the act `act`. */
export function postLangChoice(lang: string, act: string, label: string): string {
  return valueChoice({
    list: LANGUAGES.map((l) => ({ id: l.tag, label: l.endonym.toUpperCase() })),
    chosen: lang, act, floor: 'list', uid: act, label,
  });
}

/** The URL box of a webhook, and under it `stored`, the line of the masked
 *  URL the server holds, or '' for none. */
export function urlForm(hooks: DiscordHooks, box: string, placeholder: string, label: string, stored: string): string {
  return '<div class="form">' + hooks.box(box, DISCORD_URL_MAX, placeholder, label) + '</div>'
    + (stored === '' ? '' : lede(stored));
}

/** SAVE, and for a stored webhook TEST and the armed REMOVE (R837): the
 *  presses `<prefix>-save`, `<prefix>-test` and `<prefix>-remove`. */
export function webhookControls(
  gate: ArmGate, prefix: string, configured: boolean,
  w: { save: string; test: string; remove: string; remove_armed: string },
): string {
  return button(w.save, { act: `${prefix}-save` }, { variant: 'primary' })
    + (configured
      ? button(w.test, { act: `${prefix}-test` })
        + armedButton(gate, `${prefix}-remove`, w.remove, w.remove_armed, { act: `${prefix}-remove` })
      : '');
}

/** The line that follows a TEST. */
export function testLine(out: DiscordTestResult): string {
  if (out.delivered) return S.notice_test_ok;
  if (out.status === 0) return S.notice_test_silent;
  return S.notice_test_failed({ status: out.status });
}

/** The words of the state pill. A state this build does not know reads
 *  as NOT SET. */
const STATE_WORDS: ReadonlyMap<string, string> = new Map([
  ['on', S.state_on],
  ['off', S.state_off],
]);

function stateWord(v: ClanDiscord): string {
  return STATE_WORDS.get(v.state) ?? S.state_none;
}

/** Reports whether Discord turned a webhook or a relay off: it is off and
 *  carries refusals. The switch sets the count to 0. */
export function refused(v: { state: string; failures: number }): boolean {
  return v.state === 'off' && v.failures > 0;
}

/** The stored switch of a webhook or a relay. One that Discord refused
 *  shows ON, so a save of a new URL turns it on (R2422); a webhook with
 *  none stored starts ON, because a save asks for posts. */
export function switchOn(v: { state: string; failures: number }): boolean {
  return v.state !== 'off' || refused(v);
}

/** The card, as the CLAN panel mounts it. */
export interface DiscordCard {
  /** The body of the DISCORD page, or '' for a caller who may not see it. */
  html(manage: boolean): string;
  /** The state word of the webhook (NOT SET, ON or OFF), or '' while the
   *  read has not answered: the DISCORD summary of the CLAN rail and of
   *  the page title. */
  state(): string;
  /** Takes one press. Answers false for an action that is not the card's. */
  act(action: string, el: HTMLElement): boolean;
}

/** What the CLAN panel lends the card. */
export interface DiscordHooks {
  /** The panel's one write path. */
  run: RunCall;
  /** Sets the plain outcome line above the page. */
  notice(text: string): void;
  /** The trimmed text of a box, and the markup of one box. */
  typed(name: string): string;
  /** A non-empty `label` stands over the box as its visible label
   *  (R1738); the placeholder then shows an example only. */
  box(name: string, max: number, placeholder: string, label?: string): string;
  /** Empties a box after a save. */
  clear(name: string): void;
}

/** What the CLAN panel lends the DISCORD card beyond DiscordHooks: the
 *  write of a box, which shows the stored role id of a class (R1642). */
export interface DiscordCardHooks extends DiscordHooks {
  fill(name: string, value: string): void;
}

/** The edits that wait for SAVE. Null while the card shows the server. */
interface Draft {
  lang: string;
  classes: Set<string>;
  on: boolean;
}

export function createDiscordCard(ctx: PanelContext, gate: ArmGate, hooks: DiscordCardHooks): DiscordCard {
  /** The stored webhook, through the hub cache. */
  const read = (): Cell<ClanDiscord> => ctx.data.cell<ClanDiscord>(DISCORD_CELL, TTL_ON_OPEN, () => getClanDiscord());
  let draft: Draft | null = null;
  /** The read the role boxes were filled from, and the ids they took. A
   *  new read fills each box the player did not change since. */
  let seeded: { from: ClanDiscord; roles: Record<string, string> } | null = null;

  function seedRoles(v: ClanDiscord): void {
    if (seeded?.from === v) return;
    const roles = v.roles ?? {};
    for (const c of DISCORD_CLASSES) {
      const box = discordRoleBox(c);
      if (seeded === null || hooks.typed(box) === (seeded.roles[c] ?? '')) hooks.fill(box, roles[c] ?? '');
    }
    seeded = { from: v, roles: { ...roles } };
  }

  /** The draft, or the stored settings when nothing waits. */
  function current(v: ClanDiscord): Draft {
    return draft ?? { lang: v.lang, classes: new Set(v.classes), on: switchOn(v) };
  }

  /** One class switch. `name` is the class row's name, which the switch
   *  takes as its accessible name (R1738). */
  function sw(on: boolean, key: string, name: string): string {
    return switchButton(on, ` data-act="discord-class" data-key="${esc(key)}"`, name);
  }

  function drop(): void {
    draft = null;
    seeded = null;
    ctx.data.drop(DISCORD_CELL);
  }

  return {
    state(): string {
      const v = read().value;
      return v === undefined ? '' : stateWord(v);
    },

    html(manage: boolean): string {
      if (!manage) return '';
      const cell = read();
      const v = cell.value;
      if (v === undefined) return plain(pending(cell), 'discord');
      const d = current(v);
      seedRoles(v);
      const url = urlForm(hooks, DISCORD_URL_BOX, S.url_placeholder, S.url_label, v.url ? S.url_stored({ url: v.url }) : '');
      const classes = DISCORD_CLASSES.map((c) => {
        const [name, desc] = CLASS_WORDS[c];
        return srow(name, esc(desc), sw(d.classes.has(c), c, name))
          + '<div class="form">' + hooks.box(discordRoleBox(c), DISCORD_ROLE_ID_MAX, '', S.role_label) + '</div>';
      }).join('');
      // A webhook that Discord refused carries the count; a webhook that
      // the switch turned off carries none, because a save sets it to 0.
      return plain(srow(S.switch, esc(S.switch_desc), switchButton(d.on, ' data-act="discord-switch"', S.switch))
        + lede(S.lede)
        + (refused(v) ? errLine(S.off_line({ count: v.failures })) : '')
        + srow(S.url, '', '') + url
        + srow(S.lang, esc(S.lang_desc), '') + ctlRow(postLangChoice(d.lang, 'discord-lang', S.lang))
        + classes
        + lede(S.role_hint)
        + lede(S.save_hint)
        + ctlRow(webhookControls(gate, 'discord', v.configured, S))
        + lede(S.privacy),
        'discord');
    },

    act(action: string, el: HTMLElement): boolean {
      const v = ctx.data.peek<ClanDiscord>(DISCORD_CELL).value;
      switch (action) {
        case 'discord-switch': {
          if (v === undefined) return true;
          const d = current(v);
          draft = { ...d, on: !d.on };
          ctx.play('sfx-menu-toggle');
          ctx.refresh();
          return true;
        }
        case 'discord-class': {
          const key = oneOf(DISCORD_CLASSES, el.dataset.key);
          if (v === undefined || key === undefined) return true;
          const d = current(v);
          const next = new Set(d.classes);
          if (next.has(key)) next.delete(key); else next.add(key);
          draft = { ...d, classes: next };
          ctx.play('sfx-menu-toggle');
          ctx.refresh();
          return true;
        }
        case 'discord-lang': {
          const lang = postLangOf(el.dataset.id);
          if (v === undefined || lang === undefined) return true;
          draft = { ...current(v), lang };
          ctx.play('sfx-menu-press');
          ctx.refresh();
          return true;
        }
        case 'discord-save': {
          if (v === undefined) return true;
          const d = current(v);
          // The body keeps the order of the class list, as the server
          // stores it.
          const classes = DISCORD_CLASSES.filter((c) => d.classes.has(c));
          const url = hooks.typed(DISCORD_URL_BOX);
          // Every class goes, an empty box too: the body is the whole set.
          const roles = Object.fromEntries(DISCORD_CLASSES.map((c) => [c, hooks.typed(discordRoleBox(c))]));
          hooks.run(S.busy_save, () => putClanDiscord({ url, lang: d.lang, classes, roles, on: d.on }), () => {
            hooks.clear(DISCORD_URL_BOX);
            drop();
            hooks.notice(S.notice_saved);
          });
          return true;
        }
        case 'discord-test':
          hooks.run(S.busy_test, () => testClanDiscord(), (out) => {
            ctx.data.drop(DISCORD_CELL);
            hooks.notice(testLine(out));
          });
          return true;
        case 'discord-remove':
          if (!armedPress(ctx, gate, 'discord-remove')) return true;
          hooks.run(S.busy_remove, () => deleteClanDiscord(), () => {
            drop();
            hooks.notice(S.notice_removed);
          });
          return true;
        default:
          return false;
      }
    },
  };
}
